import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, BACKEND_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MUMBAI_LOCATIONS, INDIAN_BANKS, RELATIONS } from '../lib/constants';

export default function DriverForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [ifscVerifying, setIfscVerifying] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    mobile: '',
    location: '',
    pan_number: '',
    pan_image_url: '',
    aadhar_number: '',
    aadhar_front_url: '',
    aadhar_back_url: '',
    license_number: '',
    license_front_url: '',
    license_back_url: '',
    selfie_url: '',
    bank_details: {
      account_holder_name: '',
      account_number: '',
      ifsc: '',
      bank_name: '',
    },
    reference1: {
      name: '',
      mobile: '',
      relation: '',
    },
    reference2: {
      name: '',
      mobile: '',
      relation: '',
    },
  });

  useEffect(() => {
    if (isEdit) {
      loadDriver();
    }
  }, [id]);

  const loadDriver = async () => {
    try {
      const response = await api.get(`/drivers/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to load driver:', error);
      toast.error('Failed to load driver details');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const verifyIFSC = async (ifsc) => {
    if (!ifsc || ifsc.length !== 11) return;
    
    setIfscVerifying(true);
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (response.ok) {
        const data = await response.json();
        setFormData((prev) => ({
          ...prev,
          bank_details: {
            ...prev.bank_details,
            bank_name: data.BANK,
            ifsc: ifsc.toUpperCase(),
          },
        }));
        toast.success('IFSC code verified successfully');
      } else {
        toast.error('Invalid IFSC code');
      }
    } catch (error) {
      console.error('IFSC verification failed:', error);
      toast.error('IFSC verification failed');
    } finally {
      setIfscVerifying(false);
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, [fieldName]: fileUrl }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('File upload failed');
    } finally {
      setUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await api.put(`/drivers/${id}`, formData);
        toast.success('Driver updated successfully');
      } else {
        await api.post('/drivers', formData);
        toast.success('Driver created successfully');
      }
      navigate('/drivers');
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(isEdit ? 'Failed to update driver' : 'Failed to create driver');
    } finally {
      setLoading(false);
    }
  };

  const FileUploadField = ({ label, fieldName, accept = 'image/*', testId }) => (
    <div>
      <Label htmlFor={fieldName}>{label}</Label>
      <div className="mt-1 flex items-center gap-3">
        <Input
          id={fieldName}
          type="file"
          accept={accept}
          data-testid={testId}
          onChange={(e) => handleFileUpload(e, fieldName)}
          className="flex-1"
          disabled={uploading[fieldName]}
        />
        {uploading[fieldName] && <Loader2 className="animate-spin" size={20} />}
      </div>
      {formData[fieldName] && (
        <div className="mt-2">
          <img
            src={formData[fieldName]}
            alt={label}
            className="w-32 h-32 object-cover rounded border border-gray-300"
            data-testid={`${testId}-preview`}
          />
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="driver-form-page">
      <div className="mb-6">
        <Button
          onClick={() => navigate('/drivers')}
          variant="ghost"
          data-testid="back-button"
          className="mb-4"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Drivers
        </Button>
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">
          {isEdit ? 'Edit Driver' : 'Add New Driver'}
        </h1>
        <p className="text-gray-600">
          {isEdit ? 'Update driver information' : 'Fill in the driver details below'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Personal Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                name="full_name"
                data-testid="input-full-name"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="mobile">Mobile Number *</Label>
              <Input
                id="mobile"
                name="mobile"
                data-testid="input-mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                pattern="[0-9]{10}"
                title="Please enter a valid 10-digit mobile number"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.location}
                onValueChange={(value) => setFormData({ ...formData, location: value })}
              >
                <SelectTrigger data-testid="select-location">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {MUMBAI_LOCATIONS.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {loc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Documents</h2>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="pan_number">PAN Number *</Label>
                <Input
                  id="pan_number"
                  name="pan_number"
                  data-testid="input-pan-number"
                  value={formData.pan_number}
                  onChange={handleChange}
                  required
                  pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                  title="Please enter a valid PAN number (e.g., ABCDE1234F)"
                />
              </div>
              <FileUploadField
                label="PAN Card Image *"
                fieldName="pan_image_url"
                testId="upload-pan-image"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="aadhar_number">Aadhar Number *</Label>
                <Input
                  id="aadhar_number"
                  name="aadhar_number"
                  data-testid="input-aadhar-number"
                  value={formData.aadhar_number}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{12}"
                  title="Please enter a valid 12-digit Aadhar number"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FileUploadField
                  label="Aadhar Front *"
                  fieldName="aadhar_front_url"
                  testId="upload-aadhar-front"
                />
                <FileUploadField
                  label="Aadhar Back *"
                  fieldName="aadhar_back_url"
                  testId="upload-aadhar-back"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="license_number">Driving License Number *</Label>
                <Input
                  id="license_number"
                  name="license_number"
                  data-testid="input-license-number"
                  value={formData.license_number}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FileUploadField
                  label="License Front *"
                  fieldName="license_front_url"
                  testId="upload-license-front"
                />
                <FileUploadField
                  label="License Back *"
                  fieldName="license_back_url"
                  testId="upload-license-back"
                />
              </div>
            </div>

            <FileUploadField label="Driver Selfie *" fieldName="selfie_url" testId="upload-selfie" />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Bank Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="bank_details.account_holder_name">Account Holder Name *</Label>
              <Input
                id="bank_details.account_holder_name"
                name="bank_details.account_holder_name"
                data-testid="input-account-holder-name"
                value={formData.bank_details.account_holder_name}
                onChange={handleChange}
                required
                placeholder="Enter account holder name"
              />
            </div>
            <div>
              <Label htmlFor="bank_details.account_number">Account Number *</Label>
              <Input
                id="bank_details.account_number"
                name="bank_details.account_number"
                data-testid="input-account-number"
                value={formData.bank_details.account_number}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="bank_details.ifsc">IFSC Code *</Label>
              <div className="relative">
                <Input
                  id="bank_details.ifsc"
                  name="bank_details.ifsc"
                  data-testid="input-ifsc"
                  value={formData.bank_details.ifsc}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.value.length === 11) {
                      verifyIFSC(e.target.value);
                    }
                  }}
                  required
                  pattern="[A-Z]{4}[0-9]{7}"
                  title="Please enter a valid IFSC code"
                  maxLength="11"
                  placeholder="e.g., SBIN0001234"
                />
                {ifscVerifying && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="animate-spin" size={16} />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Bank name will be auto-filled on verification</p>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="bank_details.bank_name">Bank Name *</Label>
              <Select
                value={formData.bank_details.bank_name}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    bank_details: { ...prev.bank_details, bank_name: value },
                  }))
                }
              >
                <SelectTrigger data-testid="select-bank">
                  <SelectValue placeholder="Select bank or verify via IFSC" />
                </SelectTrigger>
                <SelectContent>
                  {INDIAN_BANKS.map((bank) => (
                    <SelectItem key={bank} value={bank}>
                      {bank}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">References</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Reference 1</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="reference1.name">Name *</Label>
                  <Input
                    id="reference1.name"
                    name="reference1.name"
                    data-testid="input-ref1-name"
                    value={formData.reference1.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reference1.mobile">Mobile *</Label>
                  <Input
                    id="reference1.mobile"
                    name="reference1.mobile"
                    data-testid="input-ref1-mobile"
                    value={formData.reference1.mobile}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                  />
                </div>
                <div>
                  <Label htmlFor="reference1.relation">Relation *</Label>
                  <Select
                    value={formData.reference1.relation}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        reference1: { ...prev.reference1, relation: value },
                      }))
                    }
                  >
                    <SelectTrigger data-testid="select-ref1-relation">
                      <SelectValue placeholder="Select relation" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONS.map((rel) => (
                        <SelectItem key={rel} value={rel}>
                          {rel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Reference 2</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="reference2.name">Name *</Label>
                  <Input
                    id="reference2.name"
                    name="reference2.name"
                    data-testid="input-ref2-name"
                    value={formData.reference2.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reference2.mobile">Mobile *</Label>
                  <Input
                    id="reference2.mobile"
                    name="reference2.mobile"
                    data-testid="input-ref2-mobile"
                    value={formData.reference2.mobile}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10}"
                  />
                </div>
                <div>
                  <Label htmlFor="reference2.relation">Relation *</Label>
                  <Select
                    value={formData.reference2.relation}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        reference2: { ...prev.reference2, relation: value },
                      }))
                    }
                  >
                    <SelectTrigger data-testid="select-ref2-relation">
                      <SelectValue placeholder="Select relation" />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONS.map((rel) => (
                        <SelectItem key={rel} value={rel}>
                          {rel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/drivers')}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="submit-driver-button"
            disabled={loading}
            className="bg-shizah-blue hover:bg-blue-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin mr-2" size={20} />
                Saving...
              </>
            ) : (
              <>{isEdit ? 'Update Driver' : 'Create Driver'}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
